import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const TTL = 45;
const ACTIVE_PHASES = ["notified", "payment"];
const BUSY_PHASES = ["typing", "notified", "payment"];
// Simulated baseline so the "recovered today" counter feels alive on first load.
const RECOVERED_BASELINE = 1247;

// Encapsulates the full SlotSaver FSM: seed load, webhook trigger (Agent 1+2),
// atomic lock (Agent 3), capture/booking (Agent 4), countdown and reset.
export function useSlotBooking() {
  const [providers, setProviders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle|typing|notified|payment|booked
  const [mobileTab, setMobileTab] = useState("admin"); // admin|whatsapp (mobile only)
  const [slot, setSlot] = useState(null);
  const [matching, setMatching] = useState(null);
  const [winningUser, setWinningUser] = useState(null);
  const [lock, setLock] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [bookedProviderId, setBookedProviderId] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(TTL);
  const [processingMs, setProcessingMs] = useState(null);
  const [stripeOpen, setStripeOpen] = useState(false);
  const [isTriggering, setIsTriggering] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [recoveredToday, setRecoveredToday] = useState(RECOVERED_BASELINE);
  const typingTimer = useRef(null);

  const loadStats = useCallback(async () => {
    try {
      const s = await api.getStats();
      setRecoveredToday(RECOVERED_BASELINE + (s?.gmv || 0));
    } catch (err) {
      console.error("loadStats failed", err);
    }
  }, []);

  const loadProviders = useCallback(async () => {
    try {
      const data = await api.getProviders();
      setProviders(data);
      if (data.length) setSelectedId((prev) => prev || data[0].provider_id);
    } catch (err) {
      console.error("loadProviders failed", err);
      toast.error("Unable to load sports centers");
    }
  }, []);

  useEffect(() => {
    loadProviders();
    loadStats();
  }, [loadProviders, loadStats]);

  // countdown — functional update keeps `secondsLeft` out of the dep array
  useEffect(() => {
    if (!ACTIVE_PHASES.includes(phase)) return undefined;
    const id = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [phase]);

  const resetFlowState = useCallback(() => {
    setPhase("idle");
    setSlot(null);
    setMatching(null);
    setWinningUser(null);
    setLock(null);
    setReceipt(null);
    setBookedProviderId(null);
    setSecondsLeft(TTL);
    setProcessingMs(null);
    setStripeOpen(false);
  }, []);

  const handleSimulate = useCallback(async (providerId) => {
    if (typingTimer.current) clearTimeout(typingTimer.current);
    resetFlowState();
    setIsTriggering(true);
    try {
      const res = await api.triggerCancellation(providerId);
      setSlot(res.slot);
      setMatching(res.matching);
      setProcessingMs(res.processing_ms);
      const win = res.matching.bucket_premium[0] || res.matching.bucket_standard[0] || null;
      setWinningUser(win);
      setPhase("typing");
      setIsTriggering(false);
      setMobileTab("whatsapp"); // jump to the live chat on mobile
      toast("🚨 CANCELLATION webhook received", {
        description: `${res.matching.total_matched} users in range · matching in ${res.processing_ms}ms`,
      });
      typingTimer.current = setTimeout(() => {
        setPhase("notified");
        setSecondsLeft(TTL);
      }, 2000);
    } catch (err) {
      setIsTriggering(false);
      toast.error("Webhook error", { description: err?.response?.data?.detail || "" });
    }
  }, [resetFlowState]);

  const handleBookNow = useCallback(async () => {
    if (!slot || !winningUser) return;
    setIsLocking(true);
    try {
      const res = await api.lockSlot(slot.slot_id, winningUser.user_id);
      setLock(res.lock);
      setIsLocking(false);
      setPhase("payment");
      setStripeOpen(true);
      toast.success("🔒 Atomic lock acquired", {
        description: `${winningUser.name} won the slot · TTL ${res.ttl_seconds}s`,
      });
    } catch (err) {
      setIsLocking(false);
      const d = err?.response?.data?.detail;
      toast.error("Lock failed", {
        description: typeof d === "object" ? d.message : "Slot unavailable",
      });
    }
  }, [slot, winningUser]);

  const handlePay = useCallback(async () => {
    if (!slot || !lock) return;
    setIsPaying(true);
    try {
      const rec = await api.bookSlot(slot.slot_id, lock.lock_id);
      setReceipt(rec);
      setIsPaying(false);
      setStripeOpen(false);
      setBookedProviderId(slot.provider_id);
      setPhase("booked");
      toast.success("✅ Booking confirmed!", {
        description: `${rec.booking_id} · club payout €${rec.provider_payout}`,
      });
      loadProviders();
      loadStats();
    } catch (err) {
      setIsPaying(false);
      toast.error("Payment failed", { description: err?.response?.data?.detail || "" });
    }
  }, [slot, lock, loadProviders, loadStats]);

  const handleReset = useCallback(async () => {
    if (typingTimer.current) clearTimeout(typingTimer.current);
    try {
      await api.reset();
    } catch (err) {
      console.error("reset failed", err);
      toast.error("Reset failed", { description: err?.response?.data?.detail || "" });
    }
    resetFlowState();
    loadProviders();
    loadStats();
    setMobileTab("admin");
    toast("Demo reset", { description: "Slots and bookings cleared" });
  }, [resetFlowState, loadProviders, loadStats]);

  const busy = isTriggering || BUSY_PHASES.includes(phase);

  return {
    providers, selectedId, setSelectedId,
    phase, mobileTab, setMobileTab,
    slot, matching, winningUser, receipt, bookedProviderId,
    secondsLeft, processingMs,
    recoveredToday,
    stripeOpen, setStripeOpen,
    isTriggering, isLocking, isPaying, busy,
    handleSimulate, handleBookNow, handlePay, handleReset,
  };
}

export default useSlotBooking;
